import DOMPurify from "dompurify";
import { useMemo } from "react";
// @mui
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
// hooks
import useResponsive from "~src/hooks/useResponsive";
// component
import { AdBody } from "./AdBody";
import Iconify from "~src/components/Iconify";
// type
import { TrendingAd } from "~src/types/ad";
// utils
import { fDate } from "~src/utils/formatTime";
import { PATH_BRANDS } from "~src/routes/paths";
import { logError } from "~src/utils/logger";
// ----------------------------------------------------------------------------------

export default function AdDetailView({
  ad,
  handleClose,
}: {
  ad: TrendingAd;
  handleClose?: VoidFunction;
}) {
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <AdHeader ad={ad} handleClose={handleClose} />
      <AdHtml ad={ad} />
      <AdBody ad={ad} bodyHeight={"425px"} />
    </Stack>
  );
}

type AdDetailViewProps = {
  ad: TrendingAd;
  handleClose?: VoidFunction;
};

const AdHeader = ({ ad, handleClose }: AdDetailViewProps) => {
  const isDesktop = useResponsive("up", "lg");

  const handleNavigation = () => {
    const url = PATH_BRANDS.overview(ad.brand_id);
    window.open(url, "_blank");
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
      }}
    >
      <img
        src={ad.profile_image || ""}
        style={{
          width: "61px",
          height: "61px",
          display: "inline-block",
          marginRight: "11px",
          borderRadius: "50%",
          cursor: "pointer",
        }}
        onClick={handleNavigation}
      />
      <Stack>
        <Typography
          variant="h6"
          onClick={handleNavigation}
          sx={{ cursor: "pointer" }}
        >
          {ad.brand_name}
        </Typography>

        <Typography
          sx={{
            fontWeight: "400",
            fontSize: "16px",
            color: "rgba(59, 78, 223, 0.7);"
          }}>
          {`Saved on ${fDate(ad.start_date)}`}
        </Typography>
      </Stack>
      {!isDesktop && handleClose && (
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 20, right: 15 }}
        >
          <Iconify icon={"material-symbols:close"} width={20} height={20} />
        </IconButton>
      )}
    </Box>
  );
};

const AdHtml = ({ ad }: AdDetailViewProps) => {
  const theme = useTheme();
  const badHtml = ad.ad_content?.includes("&#123;");

  // Sanitize content in useMemo to keep try/catch outside of JSX
  const sanitizedContent = useMemo(() => {
    if (!ad.ad_content || badHtml) {
      return null;
    }

    try {
      // Configure DOMPurify with stricter settings
      const sanitizerConfig = {
        FORBID_TAGS: ['script', 'iframe', 'form', 'object', 'embed'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style'],
        ALLOW_DATA_ATTR: false,
        RETURN_DOM_FRAGMENT: false,
        RETURN_DOM: false,
        SANITIZE_DOM: true
      };

      return DOMPurify.sanitize(ad.ad_content, sanitizerConfig);
    } catch (error) {
      logError(`Error sanitizing ad content: ${error}`);
      return null;
    }
  }, [ad.ad_content, badHtml]);

  // Return fallback if sanitization failed or content is problematic
  if (!sanitizedContent) {
    return (
      <Typography variant="body2" sx={{ color: "grey.400" }}>
        {ad.ad_content || "No content available"}
      </Typography>
    );
  }

  return (
    <div
      style={{ color: theme.palette.grey[400] }}
      dangerouslySetInnerHTML={{
        __html: sanitizedContent,
      }}
    />
  );
};
